<template>
  <div>
    <Head title="Home" />
    <div class="bg-white rounded-md shadow overflow-hidden mr-2">
      <form @submit.prevent="update">
        <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
          <!-- This is an example component -->
          <div class="w-full mx-auto">
            <div class="border-b border-gray-200 dark:border-gray-700 tab-head">
              <ul
                id="myTab" class="flex flex-wrap -mb-px" data-tabs-toggle="#myTabContent"
                role="tablist"
              >
                <li v-for="(tab, ti) in tabs" :key="ti" class="mr-2" role="presentation">
                  <div
                    class="inline-block text-gray-500 hover:text-gray-600 hover:border-gray-300 rounded-t-lg py-4 px-4 text-sm text-center border-transparent border-b-2 dark:text-gray-400 dark:hover:text-gray-300"
                    :class="{
                      'active': tab.active,
                      'text-green-600 border-green-600': tab.isValid,
                      'text-red-600': ti < currentTabIndex && !tab.isValid
                    }" @click="switchTab(ti)"
                  >
                    {{ tab.name }}
                    <span v-if="tab.isValid" class="ml-1 text-xs">✓</span>
                    <span v-else-if="ti < currentTabIndex" class="ml-1 text-xs">✗</span>
                  </div>
                </li>
              </ul>
            </div>
            <div class="tab-content">
              <!-- Section 1 -->
              <div class="p-4 t-content" :class="{ 'active': tabs[0].active }">
                <div
                  v-if="section1Errors.length > 0"
                  class="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400"
                >
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <icon name="alert" class="h-5 w-5 text-yellow-400" />
                    </div>
                    <div class="ml-3">
                      <p class="text-sm text-yellow-700">
                        Please fix the following errors before proceeding:
                      </p>
                      <ul class="mt-2 text-sm text-yellow-700 list-disc pl-5 space-y-1">
                        <li v-for="error in section1Errors" :key="error">{{ error }}</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <text-input
                  v-model="form.html.sections[0].title"
                  :error="form.errors['html.sections.0.title']" class="pr-6 pb-8 pt-5 w-full"
                  :label="__('Title')" required
                />
                <textarea-input
                  v-model="form.html.sections[0].details" name="content"
                  :error="form.errors['html.sections.0.details']" class="pr-6 pb-8 w-full"
                  :label="__('Details')" required
                />

                <div class="flex justify-between">
                  <h2 class="flex text-lg font-bold">Action buttons</h2>
                  <span
                    class="btn-indigo border-gray-200 rounded-full mt-1 mb-1 cursor-pointer"
                    @click="newButton(0)"
                  > + Add New </span>
                </div>

                <div
                  v-if="section1ButtonErrors.length > 0"
                  class="mb-4 p-3 bg-red-50 border border-red-200 rounded"
                >
                  <p class="text-sm text-red-600 font-medium">Button errors:</p>
                  <ul class="mt-1 text-sm text-red-600 list-disc pl-5">
                    <li v-for="error in section1ButtonErrors" :key="error">{{ error }}</li>
                  </ul>
                </div>

                <fieldset
                  v-for="(button, si) in form.html.sections[0].buttons"
                  :key="si" class="border border-solid border-gray-200 p-4 w-full mb-3 relative"
                  :class="{ 'border-red-300': hasButtonError(0, si) }"
                >
                  <legend class="text-sm">Button {{ si + 1 }}</legend>
                  <span
                    class="remove cursor-pointer items-center bg-red-700 rounded-full w-6 h-6 inline-flex justify-center absolute top-[-20px] right-[-10px] fill-white"
                    @click="removeButton(0, si)"
                  >
                    <icon name="dash" class="w-4 h-5" />
                  </span>
                  <div class="flex flex-col md:flex-row">
                    <text-input
                      v-model="button.text" class="pr-6 w-full lg:w-1/3"
                      :label="__('Text')" required
                    />
                    <text-input
                      v-model="button.link" class="w-full lg:w-1/3" :label="__('Link')"
                      required
                    />
                    <div class="flex items-center pl-5 pt-5 w-full lg:w-1/3">
                      <label class="flex toggle_swtich items-center cursor-pointer">
                        <div class="relative">
                          <input v-model="button.new_tab" type="checkbox" class="sr-only" />
                          <div class="w-10 h-4 bg-gray-400 rounded-full shadow-inner" />
                          <div
                            class="dot absolute w-6 h-6 bg-white rounded-full shadow -left-1 -top-1 transition"
                          />
                        </div>
                        <div class="ml-3 text-sm">
                          Open in new tab
                        </div>
                      </label>
                    </div>
                  </div>
                </fieldset>

                <div class="flex justify-between mt-6 pt-4 border-t">
                  <button
                    type="button" class="btn-gray" :disabled="currentTabIndex === 0"
                    @click="prevTab"
                  >
                    Previous
                  </button>
                  <button
                    type="button" class="btn-indigo" :disabled="!canGoToNextTab(0)"
                    @click="validateAndNext(0)"
                  >
                    Next Section
                  </button>
                </div>
              </div>

              <!-- Section 2 -->
              <div class="p-4 t-content" :class="{ 'active': tabs[1].active }">
                <div
                  v-if="section2Errors.length > 0"
                  class="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400"
                >
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <icon name="alert" class="h-5 w-5 text-yellow-400" />
                    </div>
                    <div class="ml-3">
                      <p class="text-sm text-yellow-700">
                        Please fix the following errors before proceeding:
                      </p>
                      <ul class="mt-2 text-sm text-yellow-700 list-disc pl-5 space-y-1">
                        <li v-for="error in section2Errors" :key="error">{{ error }}</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <text-input
                  v-model="form.html.sections[1].tagline"
                  :error="form.errors['html.sections.1.tagline']" class="pr-6 pb-8 pt-5 w-full"
                  :label="__('Tagline')"
                />
                <text-input
                  v-model="form.html.sections[1].title"
                  :error="form.errors['html.sections.1.title']" class="pr-6 pb-8 pt-5 w-full"
                  :label="__('Title')" required
                />
                <textarea-input
                  v-model="form.html.sections[1].details" name="content"
                  :error="form.errors['html.sections.1.details']" class="pr-6 pb-8 w-full"
                  :label="__('Details')" required
                />

                <div class="flex justify-between">
                  <h2 class="flex text-lg font-bold">Feature List</h2>
                  <span
                    class="btn-indigo border-gray-200 rounded-full mt-1 mb-1 cursor-pointer"
                    @click="newFeature(1)"
                  > + Add New </span>
                </div>

                <div
                  v-if="section2FeatureErrors.length > 0"
                  class="mb-4 p-3 bg-red-50 border border-red-200 rounded"
                >
                  <p class="text-sm text-red-600 font-medium">Feature errors:</p>
                  <ul class="mt-1 text-sm text-red-600 list-disc pl-5">
                    <li v-for="error in section2FeatureErrors" :key="error">{{ error }}</li>
                  </ul>
                </div>

                <fieldset
                  v-for="(feature, si) in form.html.sections[1].features"
                  :key="si" class="border border-solid border-gray-200 p-4 w-full mb-3 relative"
                  :class="{ 'border-red-300': hasFeatureError(1, si) }"
                >
                  <legend class="text-sm">Feature {{ si + 1 }}</legend>
                  <span
                    class="remove cursor-pointer items-center bg-red-700 rounded-full w-6 h-6 inline-flex justify-center absolute top-[-20px] right-[-10px] fill-white"
                    @click="removeFeature(1, si)"
                  >
                    <icon name="dash" class="w-4 h-5" />
                  </span>
                  <div class="flex flex-col md:flex-row">
                    <select-input
                      v-model="feature.icon"
                      :error="form.errors[`html.sections.1.features.${si}.icon`]"
                      class="pr-6 pb-8 w-full lg:w-1/3" :label="__('Icon')"
                    >
                      <option value="">Select an icon</option>
                      <option v-for="(l, li) in icons" :key="li" :value="l">{{ l }}</option>
                    </select-input>
                    <text-input
                      v-model="feature.title"
                      :error="form.errors[`html.sections.1.features.${si}.title`]"
                      class="pr-6 w-full lg:w-1/3" :label="__('Title')" required
                    />
                    <textarea-input
                      v-model="feature.details" name="content"
                      :error="form.errors[`html.sections.1.features.${si}.details`]"
                      class="w-full lg:w-1/3" :label="__('Details')" required
                    />
                  </div>
                </fieldset>

                <div class="flex justify-between mt-6 pt-4 border-t">
                  <button
                    type="button" class="btn-gray" :disabled="currentTabIndex === 0"
                    @click="prevTab"
                  >
                    Previous
                  </button>
                  <button
                    type="button" class="btn-indigo" :disabled="!canGoToNextTab(1)"
                    @click="validateAndNext(1)"
                  >
                    Next Section
                  </button>
                </div>
              </div>

              <!-- Section 3 -->
              <div class="p-4 t-content" :class="{ 'active': tabs[2].active }">
                <div class="flex flex-col items-start gap-[30px] pl-5 pt-5 w-full lg:w-1/3">
                  <label class="flex toggle_swtich items-center cursor-pointer">
                    <div class="relative">
                      <input
                        v-model="form.html.sections[2].enable_ticket_section" type="checkbox"
                        class="sr-only"
                      />
                      <div class="w-10 h-4 bg-gray-400 rounded-full shadow-inner" />
                      <div
                        class="dot absolute w-6 h-6 bg-white rounded-full shadow -left-1 -top-1 transition"
                      />
                    </div>
                    <div class="ml-3 text-sm">
                      {{ 'Enable Ticket Section' }}
                    </div>
                  </label>
                </div>

                <div class="flex justify-between mt-6 pt-4 border-t">
                  <button
                    type="button" class="btn-gray" :disabled="currentTabIndex === 0"
                    @click="prevTab"
                  >
                    Previous
                  </button>
                  <loading-button
                    :loading="form.processing" class="btn-indigo" type="submit"
                    :disabled="!canSubmitForm"
                  >
                    {{ __('Save') }}
                  </loading-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Link, Head } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import TextInput from '@/Shared/TextInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TextareaInput from '@/Shared/TextareaInput'
import axios from 'axios'

export default {
    metaInfo: { title: 'Home' },
    components: {
        Icon,
        Link,
        Head,
        Pagination,
        TextInput,
        SelectInput,
        LoadingButton,
        TextareaInput,
    },
    layout: Layout,
    props: {
        page: Object,
    },
    remember: 'form',
    data() {
        return {
            icons: ['apple', 'book', 'cheveron-down', 'cheveron-right', 'location', 'office', 'shopping-cart', 'store-front', 'trash', 'service', 'category', 'status', 'ticket', 'contact', 'faq', 'chat', 'knowledge', 'home', 'clock', 'settings', 'dashboard', 'edit', 'up', 'down', 'tick', 'cross', 'close', 'file', 'users', 'all_users', 'types', 'notes', 'plus', 'dash', 'check', 'post', 'no_image', 'to_up', 'angle-up', 'gear', 'phone', 'email', 'user', 'security', 'airplay', 'compass', 'aperture', 'camera', 'palette', 'login', 'page', 'send', 'send_plan', 'user_role', 'global_setting', 'image'],
            tabs: [
                { 'name': 'Section 1', 'active': true, 'isValid': false },
                { 'name': 'Section 2', 'active': false, 'isValid': false },
                { 'name': 'Section 3', 'active': false, 'isValid': false },
            ],
            currentTabIndex: 0,
            form: this.$inertia.form({
                title: 'Home',
                slug: 'home',
                is_active: this.page.is_active,
                html: JSON.parse(this.page.html),
            }),
        }
    },
    computed: {
        section1Errors() {
            const errors = []
            if (!this.form.html.sections[0]?.title?.trim()) {
                errors.push('Title is required for Section 1')
            }
            if (!this.form.html.sections[0]?.details?.trim()) {
                errors.push('Details are required for Section 1')
            }
            return errors
        },

        section2Errors() {
            const errors = []
            if (!this.form.html.sections[1]?.title?.trim()) {
                errors.push('Title is required for Section 2')
            }
            if (!this.form.html.sections[1]?.details?.trim()) {
                errors.push('Details are required for Section 2')
            }
            return errors
        },

        section1ButtonErrors() {
            const errors = []
            const buttons = this.form.html.sections[0]?.buttons || []

            buttons.forEach((button, index) => {
                if (!button.text?.trim()) {
                    errors.push(`Button ${index + 1}: Text is required`)
                }
                if (!button.link?.trim()) {
                    errors.push(`Button ${index + 1}: Link is required`)
                }
                // Validate link format
                if (button.link?.trim() && !this.isValidLink(button.link)) {
                    errors.push(`Button ${index + 1}: Please enter a valid link (starting with / or http:// or https://)`)
                }
            })

            return errors
        },

        section2FeatureErrors() {
            const errors = []
            const features = this.form.html.sections[1]?.features || []

            features.forEach((feature, index) => {
                if (!feature.title?.trim()) {
                    errors.push(`Feature ${index + 1}: Title is required`)
                }
                if (!feature.details?.trim()) {
                    errors.push(`Feature ${index + 1}: Details are required`)
                }
                if (!feature.icon?.trim()) {
                    errors.push(`Feature ${index + 1}: Please select an icon`)
                }
            })

            return errors
        },

        canSubmitForm() {
            // Form can be submitted only if all sections are valid
            return this.validateSection(0) && this.validateSection(1)
        },
    },
    watch: {
        'form.html.sections': {
            deep: true,
            handler() {
                // Update tab validity when form data changes
                this.tabs[0].isValid = this.validateSection(0)
                this.tabs[1].isValid = this.validateSection(1)
                this.tabs[2].isValid = this.validateSection(2)
            },
        },
    },
    created() {
        this.form.html.sections.map((section, index) => {
            if (section.buttons && section.buttons.length) {
                for (let i = 0; i < section.buttons.length; i++) {
                    section.buttons[i].new_tab = !!section.buttons[i].new_tab
                }
            }
            section.enable_ticket_section = !!section.enable_ticket_section
            section.login_require_create_ticket = !!section.login_require_create_ticket
            return section
        })

        // Initialize tab validation status
        this.tabs[0].isValid = this.validateSection(0)
        this.tabs[1].isValid = this.validateSection(1)
        this.tabs[2].isValid = this.validateSection(2)
    },
    methods: {
        isValidLink(link) {
            if (!link) return false
            const trimmedLink = link.trim()
            return trimmedLink.startsWith('/') ||
                trimmedLink.startsWith('http://') ||
                trimmedLink.startsWith('https://') ||
                trimmedLink.startsWith('#') ||
                trimmedLink.startsWith('mailto:') ||
                trimmedLink.startsWith('tel:')
        },

        hasButtonError(sectionIndex, buttonIndex) {
            const button = this.form.html.sections[sectionIndex]?.buttons?.[buttonIndex]
            if (!button) return false

            return !button.text?.trim() || !button.link?.trim() || !this.isValidLink(button.link)
        },

        hasFeatureError(sectionIndex, featureIndex) {
            const feature = this.form.html.sections[sectionIndex]?.features?.[featureIndex]
            if (!feature) return false

            return !feature.title?.trim() || !feature.details?.trim() || !feature.icon?.trim()
        },

        validateSection(sectionIndex) {
            if (sectionIndex === 0) {
                return this.section1Errors.length === 0 && this.section1ButtonErrors.length === 0
            } else if (sectionIndex === 1) {
                return this.section2Errors.length === 0 && this.section2FeatureErrors.length === 0
            }
            return true // Section 3 has no validation
        },

        canGoToNextTab(sectionIndex) {
            // Check if current section is valid
            if (sectionIndex === 0) {
                return this.section1Errors.length === 0 && this.section1ButtonErrors.length === 0
            } else if (sectionIndex === 1) {
                return this.section2Errors.length === 0 && this.section2FeatureErrors.length === 0
            }
            return false
        },

        validateAndNext(sectionIndex) {
            if (this.canGoToNextTab(sectionIndex)) {
                this.tabs[sectionIndex].isValid = true
                this.nextTab()
            } else {
                // Scroll to top to show validation errors
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
        },

        switchTab(tabIndex) {
            // Allow going back to previous tabs
            if (tabIndex <= this.currentTabIndex) {
                this.activeTab(tabIndex)
                this.currentTabIndex = tabIndex
            }
            // Prevent going to future tabs without validation
            else if (this.canGoToNextTab(this.currentTabIndex)) {
                this.tabs[this.currentTabIndex].isValid = true
                this.activeTab(tabIndex)
                this.currentTabIndex = tabIndex
            } else {
                // Show error for current tab
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }
        },

        nextTab() {
            const nextIndex = this.currentTabIndex + 1
            if (nextIndex < this.tabs.length) {
                this.activeTab(nextIndex)
                this.currentTabIndex = nextIndex
            }
        },

        prevTab() {
            const prevIndex = this.currentTabIndex - 1
            if (prevIndex >= 0) {
                this.activeTab(prevIndex)
                this.currentTabIndex = prevIndex
            }
        },

        activeTab(index) {
            for (const tab_item of this.tabs) {
                tab_item.active = false
            }
            this.tabs[index].active = true
        },

        newButton(index) {
            if (this.form.html.sections[index] && this.form.html.sections[index].buttons) {
                this.form.html.sections[index].buttons.push({
                    text: '',
                    link: '',
                    new_tab: false,
                })
            }
        },

        newFeature(index) {
            if (this.form.html.sections[index] && this.form.html.sections[index].features) {
                this.form.html.sections[index].features.push({
                    icon: '',
                    title: '',
                    details: '',
                })
            }
        },

        removeButton(si, index) {
            if (this.form.html.sections[si] && this.form.html.sections[si].buttons) {
                this.form.html.sections[si].buttons.splice(index, 1)
            }
        },

        removeFeature(si, index) {
            if (this.form.html.sections[si] && this.form.html.sections[si].features) {
                this.form.html.sections[si].features.splice(index, 1)
            }
        },

        update() {
            // Final validation before submission
            let isValid = true

            if (!this.validateSection(0)) {
                this.activeTab(0)
                this.currentTabIndex = 0
                isValid = false
                window.scrollTo({ top: 0, behavior: 'smooth' })
            } else if (!this.validateSection(1)) {
                this.activeTab(1)
                this.currentTabIndex = 1
                isValid = false
                window.scrollTo({ top: 0, behavior: 'smooth' })
            }

            if (isValid) {
                this.form.put(this.route('front_pages.update', 'home'))
            }
        },

        fileInputChange(e, index) {
            let selectedFiles = e.target.files
            this.form.processing = true
            if (selectedFiles.length) {
                let data = new FormData()
                data.append('image', selectedFiles[0])
                axios.post(this.route('upload.image'), data).then((response) => {
                    if (response.data && response.data.image) {
                        this.form.html.sections[index].image = response.data.image
                        this.form.processing = false
                    } else {
                        alert('something went wrong!')
                        this.form.processing = false
                    }
                }).catch((error) => {
                    this.form.processing = false
                    console.log(error)
                })
            }
        },

        fileRemove(index) {
            this.form.html.sections[index].image = null
        },

        getFileSize(size) {
            const i = Math.floor(Math.log(size) / Math.log(1024))
            return (size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i]
        },

        fileBrowse(index) {
            this.$refs['section' + index + 'image'].click()
        },
    },
}
</script>

<style scoped>
.tab-content .t-content {
    display: none;
}

.tab-content .t-content.active {
    display: block;
}

.tab-head .active {
    color: #4f46e5;
    border-color: #4f46e5;
    border-bottom: 2px solid;
}

.toggle_swtich input:checked~.dot {
    transform: translateX(100%);
    background-color: #4f46e5;
}

.toggle_swtich input:checked~.dot~.w-10 {
    background-color: #a5b4fc;
}

.text-green-600 {
    color: #16a34a;
}

.border-green-600 {
    border-color: #16a34a;
}

.text-red-600 {
    color: #dc2626;
}

.btn-gray {
    background-color: #f3f4f6;
    color: #374151;
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    font-weight: 500;
    transition: background-color 0.2s;
    border: 1px solid #d1d5db;
}

.btn-gray:hover:not(:disabled) {
    background-color: #e5e7eb;
}

.btn-gray:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-indigo:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #a5b4fc;
}

.btn-indigo {
    background-color: #4f46e5;
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    font-weight: 500;
    transition: background-color 0.2s;
    border: 1px solid #4f46e5;
}

.btn-indigo:hover:not(:disabled) {
    background-color: #4338ca;
}
</style>
